import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { MessageCircle, ListChecks, Plus, Trash2, UserCog, Scissors, Link2Off, FileDown, Check } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast } from "sonner";
import api, { fmtARS, apiError } from "@/lib/api";

const TASK_TYPES = ["corte", "armado", "soldadura", "instalacion", "pintura", "otro"];
const PLAN_STATUS = { pendiente: "Pendiente", en_proceso: "En proceso", terminado: "Terminado" };
const PLAN_STATUS_COLOR = {
  pendiente: "text-neutral-400 border-neutral-700",
  en_proceso: "text-blue-400 border-blue-500/40",
  terminado: "text-green-400 border-green-500/40",
};

const COLUMNS = [
  { key: "pendiente", label: "Pendiente", color: "border-t-[#F59E0B]" },
  { key: "en_proceso", label: "En proceso", color: "border-t-blue-500" },
  { key: "terminada", label: "Terminada", color: "border-t-green-500" },
  { key: "lista_para_entrega", label: "Lista p/ entregar", color: "border-t-purple-500" },
  { key: "entregada", label: "Entregada", color: "border-t-neutral-500" },
  { key: "colocada", label: "Colocada", color: "border-t-neutral-500" },
];

export default function WorkOrders() {
  const [orders, setOrders] = useState([]);
  const [team, setTeam] = useState([]);
  const [taskEdit, setTaskEdit] = useState(null);
  const [plans, setPlans] = useState([]);
  const navigate = useNavigate();

  const load = () => api.get("/orders").then((r) => setOrders(r.data)).catch(() => {});
  const loadPlans = () => api.get("/cuts/plans").then((r) => setPlans(r.data)).catch(() => {});
  useEffect(() => {
    load();
    loadPlans();
    api.get("/users").then((r) => setTeam(r.data)).catch(() => {});
  }, []);

  const linkPlan = async (planId, orderId) => {
    try {
      await api.patch(`/cuts/plans/${planId}`, { work_order_id: orderId });
      loadPlans();
    } catch (e) { toast.error(apiError(e)); }
  };

  const assignPlan = async (planId, userId) => {
    try {
      await api.patch(`/cuts/plans/${planId}`, { assigned_to: userId || null });
      loadPlans();
      load();
      toast.success(userId ? "Plan de corte asignado — se vinculó/creó la orden con la tarea de corte" : "Asignación quitada");
    } catch (e) { toast.error(apiError(e)); }
  };

  const setPlanStatus = async (planId, status) => {
    try {
      await api.patch(`/cuts/plans/${planId}`, { status });
      loadPlans();
    } catch (e) { toast.error(apiError(e)); }
  };

  const downloadPlanPdf = async (plan) => {
    try {
      const res = await api.get(`/cuts/plans/${plan.id}/pdf`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = `plan-corte-${plan.name}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) { toast.error(apiError(e)); }
  };

  const removePlan = async (plan) => {
    if (!window.confirm(`Eliminar el plan de corte "${plan.name}" definitivamente?`)) return;
    try {
      await api.delete(`/cuts/plans/${plan.id}`);
      loadPlans();
      toast.success("Plan de corte eliminado");
    } catch (e) { toast.error(apiError(e)); }
  };

  const assign = async (id, userId) => {
    try {
      await api.patch(`/orders/${id}/assign`, { user_id: userId || null });
      load();
      toast.success(userId ? "Orden asignada — el operario recibirá una notificación" : "Asignación quitada");
    } catch (e) { toast.error(apiError(e)); }
  };

  const openTasks = (o) => setTaskEdit({ orderId: o.id, number: o.number, tasks: (o.tasks || []).map((t) => ({ type: t.type, description: t.description, done: !!t.done })) });

  const saveTasks = async () => {
    const tasks = taskEdit.tasks.filter((t) => t.description.trim()).map(({ type, description }) => ({ type, description }));
    try {
      await api.put(`/orders/${taskEdit.orderId}/tasks`, { tasks });
      setTaskEdit(null);
      load();
      toast.success("Tareas guardadas");
    } catch (e) { toast.error(apiError(e)); }
  };

  const setStatus = async (id, status) => {
    try { await api.patch(`/orders/${id}`, { status }); load(); } catch (e) { toast.error(apiError(e)); }
  };

  const removeOrder = async (o) => {
    if (!window.confirm(`Eliminar la orden ${o.number} definitivamente? Tambien se eliminan sus planes de corte vinculados.`)) return;
    try {
      await api.delete(`/orders/${o.id}`);
      load();
      loadPlans();
      toast.success("Orden eliminada");
    } catch (e) { toast.error(apiError(e)); }
  };

  return (
    <div data-testid="orders-page">
      <h1 className="font-heading font-bold uppercase text-2xl tracking-tight mb-6">Órdenes de trabajo</h1>
      {orders.length === 0 && <p className="text-neutral-500 text-sm mb-4">No hay órdenes. Podés crearlas directamente desde el botón + o convertir una cotización.</p>}
      <div className="grid md:grid-cols-2 xl:grid-cols-3 gap-4">
        {COLUMNS.map((col) => {
          const items = orders.filter((o) => o.status === col.key);
          return (
            <div key={col.key} className={`bg-[#111] border border-neutral-800 border-t-2 ${col.color} rounded-md p-4 min-h-[300px]`} data-testid={`order-column-${col.key}`}>
              <div className="flex justify-between items-center mb-4">
                <h2 className="font-heading font-bold uppercase text-sm tracking-wider">{col.label}</h2>
                <span className="text-xs font-mono text-neutral-500">{items.length}</span>
              </div>
              <div className="space-y-3">
                {items.map((o) => (
                  <div key={o.id} className="bg-[#171717] border border-neutral-800 rounded-md p-4" data-testid={`order-card-${o.id}`}>
                    <div className="flex justify-between items-center mb-1">
                      <span className="font-mono text-xs text-[#F2B90D]">{o.number}</span>
                      <span className="font-mono text-xs text-neutral-400">{fmtARS(o.total)}</span>
                    </div>
                    <p className="text-sm font-medium">{o.title}</p>
                    <p className="text-xs text-neutral-500 mt-1">{o.client_name} · {o.quote_number || "Orden directa"}</p>
                    {o.scheduled_date && <p className="text-[11px] text-[#F2B90D] mt-1">Agendada: {new Date(`${o.scheduled_date}T12:00:00`).toLocaleDateString("es-AR")}</p>}
                    {(o.tasks || []).length > 0 && (
                      <p className="text-[11px] text-neutral-400 mt-1.5 font-mono">
                        ✓ {(o.tasks || []).filter((t) => t.done).length}/{(o.tasks || []).length} tareas
                      </p>
                    )}
                    {plans.filter((p) => p.work_order_id === o.id).length > 0 && (
                      <p className="text-[11px] text-neutral-400 mt-1 font-mono flex items-center gap-1">
                        <Scissors className="w-3 h-3 shrink-0" />
                        {plans.filter((p) => p.work_order_id === o.id && p.status === "terminado").length}/{plans.filter((p) => p.work_order_id === o.id).length} planes cortados
                      </p>
                    )}
                    <div className="flex items-center gap-1.5 mt-2">
                      <UserCog className="w-3.5 h-3.5 text-neutral-500 shrink-0" />
                      <select value={o.assigned_to || ""} onChange={(e) => assign(o.id, e.target.value)}
                        className="flex-1 h-7 bg-neutral-900 border border-neutral-800 rounded-sm px-1 text-[11px]" data-testid={`order-assign-select-${o.id}`}>
                        <option value="">Sin asignar</option>
                        {team.map((u) => <option key={u.id} value={u.id}>{u.name}</option>)}
                      </select>
                    </div>
                    <div className="flex gap-1 mt-2 flex-wrap items-center">
                      <select value={o.status} onChange={(e) => setStatus(o.id, e.target.value)}
                        className="flex-1 h-7 bg-neutral-900 border border-neutral-800 rounded-sm px-1 text-[11px] uppercase font-bold" data-testid={`order-status-select-${o.id}`}>
                        {COLUMNS.map((c) => <option key={c.key} value={c.key}>{c.label}</option>)}
                      </select>
                      <button onClick={() => openTasks(o)}
                        className="text-[#F2B90D] hover:bg-[#F2B90D]/10 p-1.5 rounded-sm" title="Editar tareas" data-testid={`order-tasks-btn-${o.id}`}>
                        <ListChecks className="w-4 h-4" />
                      </button>
                      <button onClick={() => openTasks(o)}
                        className="text-neutral-400 hover:bg-neutral-800 hover:text-white p-1.5 rounded-sm relative" title="Planes de corte" data-testid={`order-plans-btn-${o.id}`}>
                        <Scissors className="w-4 h-4" />
                        {plans.filter((p) => p.work_order_id === o.id).length > 0 && (
                          <span className="absolute -top-1 -right-1 bg-[#F2B90D] text-black text-[9px] font-bold rounded-full w-3.5 h-3.5 flex items-center justify-center">
                            {plans.filter((p) => p.work_order_id === o.id).length}
                          </span>
                        )}
                      </button>
                      <button onClick={() => navigate("/panel/whatsapp", { state: { nombre: o.client_name, numero: o.number, clientId: o.client_id } })}
                        className="text-[#25D366] hover:bg-[#25D366]/10 p-1.5 rounded-sm" title="Avisar por WhatsApp" data-testid={`order-whatsapp-${o.id}`}>
                        <MessageCircle className="w-4 h-4" />
                      </button>
                      <button onClick={() => removeOrder(o)}
                        className="text-red-400 hover:bg-red-500/10 p-1.5 rounded-sm" title="Eliminar orden" data-testid={`order-delete-${o.id}`}>
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          );
        })}
      </div>

      <Dialog open={!!taskEdit} onOpenChange={(o) => !o && setTaskEdit(null)}>
        <DialogContent className="bg-[#171717] border-neutral-800 text-white max-h-[85vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="font-heading uppercase">Tareas y planes de corte de {taskEdit?.number}</DialogTitle></DialogHeader>
          {taskEdit && (
            <div className="space-y-6">
              <div className="space-y-3">
                <p className="text-xs text-neutral-400">Definí las tareas (cortes, armado, soldadura...) que el operario verá en su dashboard.</p>
                <div className="space-y-2">
                  {taskEdit.tasks.map((t, i) => (
                    <div key={i} className="flex gap-2 items-center">
                      {t.done && <Check className="w-4 h-4 text-green-400 shrink-0" />}
                      <select value={t.type} onChange={(e) => setTaskEdit({ ...taskEdit, tasks: taskEdit.tasks.map((x, xi) => xi === i ? { ...x, type: e.target.value } : x) })}
                        className="h-9 bg-neutral-900 border border-neutral-800 rounded-sm px-2 text-xs w-28" data-testid={`task-type-${i}`}>
                        {TASK_TYPES.map((tt) => <option key={tt} value={tt}>{tt}</option>)}
                      </select>
                      <Input placeholder="Descripción (ej: Cortar 12 barras según plan)" value={t.description}
                        onChange={(e) => setTaskEdit({ ...taskEdit, tasks: taskEdit.tasks.map((x, xi) => xi === i ? { ...x, description: e.target.value } : x) })}
                        className={`bg-neutral-900 border-neutral-800 rounded-sm h-9 flex-1 ${t.done ? "text-neutral-500 line-through" : ""}`} data-testid={`task-desc-${i}`} />
                      <button onClick={() => setTaskEdit({ ...taskEdit, tasks: taskEdit.tasks.filter((_, xi) => xi !== i) })} className="text-neutral-500 hover:text-red-400" data-testid={`task-delete-${i}`}>
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  ))}
                  {taskEdit.tasks.length === 0 && <p className="text-xs text-neutral-500">Todavía no hay tareas cargadas.</p>}
                </div>
                <Button size="sm" variant="outline" onClick={() => setTaskEdit({ ...taskEdit, tasks: [...taskEdit.tasks, { type: "corte", description: "", done: false }] })}
                  className="border-neutral-700 rounded-sm text-xs hover:bg-neutral-800 hover:text-white" data-testid="add-task-btn">
                  <Plus className="w-3 h-3 mr-1" /> Agregar tarea
                </Button>
                <Button onClick={saveTasks} className="w-full bg-[#F2B90D] hover:bg-[#DFA90C] text-black rounded-sm font-heading uppercase" data-testid="save-tasks-btn">Guardar tareas</Button>
              </div>

              <div className="space-y-3 pt-4 border-t border-neutral-800" data-testid="order-plans-section">
                <label className="text-xs uppercase font-bold text-neutral-400 flex items-center gap-1.5"><Scissors className="w-3.5 h-3.5" /> Planes de corte</label>
                <div className="space-y-2">
                  {plans.filter((p) => p.work_order_id === taskEdit.orderId).map((p) => (
                    <div key={p.id} className="bg-neutral-900 border border-neutral-800 rounded-sm px-3 py-2" data-testid={`order-plan-row-${p.id}`}>
                      <div className="flex items-center justify-between gap-2">
                        <div className="min-w-0">
                          <p className="text-sm truncate">{p.name}</p>
                          <p className="text-[11px] text-neutral-500 font-mono">{p.result.total_bars} barras · {(p.result.total_waste_mm / 1000).toFixed(2)}m desperdicio</p>
                        </div>
                        <div className="flex gap-1 shrink-0">
                          <button onClick={() => downloadPlanPdf(p)} className="text-neutral-400 hover:text-[#F2B90D] p-1" title="Descargar PDF" data-testid={`order-plan-pdf-${p.id}`}>
                            <FileDown className="w-4 h-4" />
                          </button>
                          <button onClick={() => linkPlan(p.id, null)} className="text-neutral-500 hover:text-[#F2B90D] p-1" title="Desvincular" data-testid={`order-plan-unlink-${p.id}`}>
                            <Link2Off className="w-4 h-4" />
                          </button>
                          <button onClick={() => removePlan(p)} className="text-red-400 hover:bg-red-500/10 p-1 rounded-sm" title="Eliminar plan" data-testid={`order-plan-delete-${p.id}`}>
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      </div>
                      <div className="mt-2 flex flex-wrap items-center gap-2">
                        <UserCog className="w-3.5 h-3.5 text-neutral-500" />
                        <select value={p.assigned_to || ""} onChange={(e) => assignPlan(p.id, e.target.value)}
                          className="h-8 bg-[#171717] border border-neutral-800 rounded-sm px-2 text-xs" data-testid={`order-plan-assign-${p.id}`}>
                          <option value="">Sin asignar</option>
                          {team.map((u) => <option key={u.id} value={u.id}>{u.name}</option>)}
                        </select>
                        <select value={p.status || "pendiente"} onChange={(e) => setPlanStatus(p.id, e.target.value)}
                          className={`h-8 bg-[#171717] border rounded-sm px-2 text-xs font-bold uppercase ${PLAN_STATUS_COLOR[p.status] || PLAN_STATUS_COLOR.pendiente}`} data-testid={`order-plan-status-${p.id}`}>
                          {Object.entries(PLAN_STATUS).map(([k, label]) => <option key={k} value={k} className="bg-neutral-900 text-white">{label}</option>)}
                        </select>
                      </div>
                    </div>
                  ))}
                  {plans.filter((p) => p.work_order_id === taskEdit.orderId).length === 0 && (
                    <p className="text-xs text-neutral-500">Todavía no hay planes de corte vinculados a esta orden.</p>
                  )}
                </div>
                <select value="" onChange={(e) => { if (e.target.value) linkPlan(e.target.value, taskEdit.orderId); }}
                  className="w-full h-9 bg-neutral-900 border border-neutral-800 rounded-sm px-2 text-xs" data-testid="order-plan-attach-select">
                  <option value="">— Vincular un plan sin asignar —</option>
                  {plans.filter((p) => !p.work_order_id).map((p) => (
                    <option key={p.id} value={p.id}>{p.name} ({p.result.total_bars} barras)</option>
                  ))}
                </select>
              </div>
              <Button variant="outline" onClick={() => navigate("/panel/optimizador")}
                className="w-full border-neutral-700 rounded-sm text-xs hover:bg-neutral-800 hover:text-white" data-testid="order-plans-new-btn">
                <Scissors className="w-3.5 h-3.5 mr-1" /> Crear nuevo plan en el optimizador
              </Button>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
